export type SpatialPoint={x:number;y:number;z:number};
export type NormalizedPoint={x:number;y:number};

export type SpatialFishMeasurement={
  start:SpatialPoint;
  end:SpatialPoint;
  measuredLengthMm:number;
  method:"webxr_depth_ai_endpoints";
  confidenceLabel:"device_spatial_tracking_estimate";
};

export type SpatialCalibrationSample={
  expectedLengthMm:number;
  measuredLengthMm:number;
  errorMm:number;
  errorPercent:number;
};

export type SpatialCalibrationSummary={
  sampleCount:number;
  meanErrorMm:number;
  meanAbsoluteErrorMm:number;
  meanAbsoluteErrorPercent:number;
  maximumAbsoluteErrorMm:number;
  passesDevelopmentTarget:boolean;
};

export const spatialDistanceMeters=(start:SpatialPoint,end:SpatialPoint)=>
  Math.hypot(end.x-start.x,end.y-start.y,end.z-start.z);

export const spatialLengthMm=(start:SpatialPoint,end:SpatialPoint)=>
  Math.round(spatialDistanceMeters(start,end)*1000);

export const viewPointAtDepth=(point:NormalizedPoint,depthMeters:number,projection:ArrayLike<number>):SpatialPoint=>{
  const ndcX=point.x*2-1,ndcY=1-point.y*2,m0=Number(projection[0]),m5=Number(projection[5]),m8=Number(projection[8]??0),m9=Number(projection[9]??0);
  return{x:(ndcX+m8)*depthMeters/m0,y:(ndcY+m9)*depthMeters/m5,z:-depthMeters};
};

export const automaticSpatialLengthMm=(start:NormalizedPoint,end:NormalizedPoint,startDepth:number,endDepth:number,projection:ArrayLike<number>)=>
  spatialLengthMm(viewPointAtDepth(start,startDepth,projection),viewPointAtDepth(end,endDepth,projection));

export const spatialCalibrationSample=(expectedLengthMm:number,measuredLengthMm:number):SpatialCalibrationSample=>{
  const errorMm=measuredLengthMm-expectedLengthMm;
  return{expectedLengthMm,measuredLengthMm,errorMm,errorPercent:errorMm/expectedLengthMm*100};
};

export const summarizeSpatialCalibration=(samples:SpatialCalibrationSample[]):SpatialCalibrationSummary=>{
  if(!samples.length)return{sampleCount:0,meanErrorMm:0,meanAbsoluteErrorMm:0,meanAbsoluteErrorPercent:0,maximumAbsoluteErrorMm:0,passesDevelopmentTarget:false};
  const mean=(values:number[])=>values.reduce((sum,value)=>sum+value,0)/values.length;
  const absoluteErrors=samples.map(sample=>Math.abs(sample.errorMm));
  const meanAbsoluteErrorPercent=mean(samples.map(sample=>Math.abs(sample.errorPercent)));
  return{
    sampleCount:samples.length,
    meanErrorMm:mean(samples.map(sample=>sample.errorMm)),
    meanAbsoluteErrorMm:mean(absoluteErrors),
    meanAbsoluteErrorPercent,
    maximumAbsoluteErrorMm:Math.max(...absoluteErrors),
    passesDevelopmentTarget:samples.length>=5&&meanAbsoluteErrorPercent<=3&&Math.max(...absoluteErrors)<=10,
  };
};
